import type { LoginPayload, ProfilePayload } from "@/api/endpoints/common/auth"
import type { AppUser, RouteMetaAuthRole } from "@/types/auth"
import type { APIError } from "@/types/common"
import type { Doctor } from "@/types/doctor"
import type { Nullable } from "@/types/utils"
import { dataURLToBlob } from "blob-util"
import _castArray from "lodash/castArray"
import _toNumber from "lodash/toNumber"
import { acceptHMRUpdate, defineStore } from "pinia"
import SecureLSModule from "secure-ls"
import { v4 as uuidv4 } from "uuid"
import Api from "@/api"
import { DEFAULT_VISIT_DURATION_MINUTES } from "@/const"
import { RouteRole, UserRole } from "@/types/auth"
import { file2Base64 } from "@/utils"

type SecureLSConstructor = typeof SecureLSModule

const SecureLS: SecureLSConstructor =
	typeof SecureLSModule === "function"
		? SecureLSModule
		: (SecureLSModule as unknown as { default: SecureLSConstructor }).default
const ls = new SecureLS({ encodingType: "aes", isCompression: false })

export const useAuthStore = defineStore("auth.common", {
	state: () => ({
		user: {
			access_token: null,
			medical_center: null,
			username: null,
			role: null,
			picture: null,
			created_at: null,
			validated: null
		} as Nullable<AppUser>,
		profile: null as Doctor | null,
		loadingProfile: false,
		loadingPicture: false,
		tokenDebounceTime: _toNumber(import.meta.env.VITE_TOKEN_DEBOUNCE_TIME) as number, // seconds
		fingerprint: null as string | null,
		askTotpSetup: false,
		notAskTotpSetupAgain: false
	}),
	actions: {
		setLogged(user: Doctor): AppUser {
			this.user = {
				access_token: user.token,
				medical_center: user.details.medical_centers[0]?.id || null,
				username: `${user.name} ${user.surname}` || "",
				role: user.role,
				picture: user.picture?.[100] || null,
				created_at: user.created_at,
				validated: user.details.is_validated
			}

			this.setProfile(user)

			return this.user as AppUser
		},
		setProfile(user: Doctor) {
			this.profile = user
		},
		setToken(token: string) {
			this.user.access_token = token
		},
		setFingerprint() {
			this.fingerprint = `${uuidv4()}-web`
		},
		ensureFingerprint() {
			if (!this.fingerprint) {
				this.setFingerprint()
			}
		},
		setMedicalCenter(id: number) {
			this.user.medical_center = id
		},
		setPicture(picture: string | null) {
			this.user.picture = picture
		},
		stopAskTotpSetupDialog() {
			this.notAskTotpSetupAgain = true
		},
		setLogout() {
			this.user = {
				access_token: null,
				medical_center: null,
				username: null,
				role: null,
				picture: null,
				created_at: null,
				validated: null
			}
		},
		async login(payload: LoginPayload): Promise<AppUser> {
			try {
				const response = await Api.common.auth.login(payload)

				if (response.data.token) {
					return this.setLogged(response.data)
				} else {
					throw new Error("token not found")
				}
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		},
		async loadProfile(): Promise<Doctor | null> {
			if (this.loadingProfile) {
				return null
			}

			this.loadingProfile = true

			try {
				const response = await Api.common.auth.getProfile()
				this.setProfile(response.data)
				return response.data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingProfile = false
			}
		},
		async updateProfile(payload: Partial<ProfilePayload>): Promise<Doctor> {
			this.loadingProfile = true

			try {
				const response = await Api.common.auth.updateProfile(payload)
				this.setProfile(response.data)
				return response.data
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingProfile = false
			}
		},
		async updatePicture(picture: Blob | string | null): Promise<void> {
			this.loadingPicture = true

			const payload = typeof picture === "string" ? dataURLToBlob(picture) : picture

			try {
				await Api.common.auth.updatePicture(payload)
				const datauri = !picture || typeof picture === "string" ? picture : await file2Base64(picture)
				return this.setPicture(datauri)
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			} finally {
				this.loadingPicture = false
			}
		}
		/*
		async refreshToken(): Promise<LoginResponse> {
			try {
				const response = await Api.common.auth.refresh(this.userToken)
				if (response.data.token) {
					this.setToken(response.data.token)
					return response.data
				} else {
					throw new Error("token not found")
				}
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		}
		*/
	},
	getters: {
		userToken(state): string {
			return state.user?.access_token || ""
		},
		userMedicalCenter(state): number {
			return state.user?.medical_center || 0
		},
		userName(state): string {
			return state.user?.username || ""
		},
		userPicture(state): string {
			return state.user?.picture || ""
		},
		userRole(state): UserRole | null {
			return state.user?.role
		},
		isLogged(state): boolean {
			return !!state.user?.access_token
		},
		isVerified(state): boolean {
			return !!state.user?.validated
		},
		isTotpConfigured(state): boolean {
			return !!state.profile?.totp_configured
		},
		isDoctor(): boolean {
			return this.userRole === UserRole.DOCTOR
		},
		isSecretary(): boolean {
			return this.userRole === UserRole.SECRETARY
		},
		isRoleGranted() {
			return (roles?: RouteMetaAuthRole | RouteMetaAuthRole[]) => {
				if (!roles) {
					return true
				}
				if (!this.userRole) {
					return false
				}

				const arrRoles: RouteMetaAuthRole[] = _castArray(roles)

				if (arrRoles.includes(RouteRole.ALL)) {
					return true
				}

				return arrRoles.includes(this.userRole)
			}
		},
		documentStatus(): "ACCEPTED" | "REFUSED" | "PENDING" | "NONE" {
			const details = this.profile?.details
			if (!details) {
				return "NONE"
			}

			if (details.documents_accepted_at) {
				return "ACCEPTED"
			}

			if (details.documents_refused_at) {
				return "REFUSED"
			}

			if (details.documents_uploaded_at && !details.documents_accepted_at && !details.documents_refused_at) {
				return "PENDING"
			}

			return "NONE"
		},
		avgVisitDuration(state): number {
			return state.profile?.details.avg_visit_duration ?? DEFAULT_VISIT_DURATION_MINUTES
		}
	},
	persist: {
		storage: {
			getItem: key => ls.get(key),
			setItem: (key, value) => ls.set(key, value)
		},
		pick: ["user", "fingerprint", "notAskTotpSetupAgain"]
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
